import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Plus, Trash2, AlertCircle, Users } from 'lucide-react';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { BoardInvitation } from '../types';
import { getPendingInvitationsForBoard } from '../firebase/firestore';

interface InviteUsersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInviteUsers: (invitations: Omit<BoardInvitation, 'id' | 'createdAt' | 'expiresAt'>[]) => void;
  boardName: string;
  boardId: string;
}

interface InvitationForm {
  email: string;
  role: 'admin' | 'editor' | 'viewer';
}

const InviteUsersModal: React.FC<InviteUsersModalProps> = ({
  isOpen,
  onClose,
  onInviteUsers,
  boardName,
  boardId,
}) => {
  const [invitations, setInvitations] = useState<InvitationForm[]>([{ email: '', role: 'editor' }]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingInvitations, setExistingInvitations] = useState<BoardInvitation[]>([]);
  const [, setIsLoadingExisting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validInvitations = invitations.filter(inv => inv.email.trim() && inv.email.includes('@'));
    
    if (validInvitations.length === 0) return;

    setIsSubmitting(true);
    setError(null);
    
    try {
      const invitationData = validInvitations.map(inv => ({
        boardId: '', // Will be set by parent
        email: inv.email.trim().toLowerCase(),
        role: inv.role,
        invitedBy: '', // Will be set by parent
        status: 'pending' as const,
      }));

      await onInviteUsers(invitationData);
      onClose();
      resetForm();
      // Reload existing invitations
      loadExistingInvitations();
    } catch (error: any) {
      console.error('Error inviting users:', error);
      setError(error.message || 'Failed to send invitations');
    } finally {
      setIsSubmitting(false);
    }
  };

  const addInvitation = () => {
    setInvitations([...invitations, { email: '', role: 'editor' }]);
  };

  const removeInvitation = (index: number) => {
    if (invitations.length > 1) {
      setInvitations(invitations.filter((_, i) => i !== index));
    }
  };

  const updateInvitation = (index: number, field: keyof InvitationForm, value: string) => {
    const newInvitations = [...invitations];
    newInvitations[index] = { ...newInvitations[index], [field]: value };
    setInvitations(newInvitations);
  };

  const resetForm = () => {
    setInvitations([{ email: '', role: 'editor' }]);
  };

  const loadExistingInvitations = async () => {
    setIsLoadingExisting(true);
    try {
      const invitations = await getPendingInvitationsForBoard(boardId);
      setExistingInvitations(invitations);
    } catch (error) {
      console.error('Error loading existing invitations:', error);
    } finally {
      setIsLoadingExisting(false);
    }
  };

  useEffect(() => {
    if (isOpen && boardId) {
      loadExistingInvitations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadExistingInvitations is stable
  }, [isOpen, boardId]);

  // Add escape key functionality
  useEscapeKey(onClose, isOpen);

  const getRoleDescription = (role: string) => {
    switch (role) {
      case 'admin':
        return 'Can manage board settings and invite users';
      case 'editor':
        return 'Can create, edit, and delete products';
      case 'viewer':
        return 'Can only view products and board content';
      default:
        return '';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-white/15 backdrop-blur-xl z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="glass-effect rounded-3xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Invite Users</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
                <AlertCircle size={16} className="text-red-500" />
                <span className="text-sm text-red-700">{error}</span>
              </div>
            )}

            {/* Existing Invitations */}
            {existingInvitations.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                  <Users size={16} className="mr-2" />
                  Pending Invitations ({existingInvitations.length})
                </h3>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {existingInvitations.map((invitation) => (
                    <div key={invitation.id} className="flex items-center justify-between p-2 bg-white/15 rounded-lg">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-800">{invitation.email}</p>
                        <p className="text-xs text-gray-600 capitalize">{invitation.role}</p>
                      </div>
                      <span className="text-xs text-gray-500">
                        {invitation.createdAt ? new Date(invitation.createdAt).toLocaleDateString() : 'Unknown date'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Invitations List */}
              <div className="space-y-4">
                {invitations.map((invitation, index) => (
                  <div key={index} className="flex items-start space-x-3 p-4 bg-white/15 rounded-xl border border-white/30">
                    <div className="flex-1 space-y-3">
                      {/* Email */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email Address
                        </label>
                        <input
                          type="email"
                          value={invitation.email}
                          onChange={(e) => updateInvitation(index, 'email', e.target.value)}
                          className="input-field w-full"
                          placeholder="user@example.com"
                          required
                        />
                      </div>

                      {/* Role */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Role
                        </label>
                        <select
                          value={invitation.role}
                          onChange={(e) => updateInvitation(index, 'role', e.target.value as any)}
                          className="input-field w-full"
                        >
                          <option value="viewer">Viewer</option>
                          <option value="editor">Editor</option>
                          <option value="admin">Admin</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          {getRoleDescription(invitation.role)}
                        </p>
                      </div>
                    </div>

                    {/* Remove Button */}
                    {invitations.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeInvitation(index)}
                        className="text-red-500 hover:text-red-700 transition-colors p-1"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Add More Button */}
              <button
                type="button"
                onClick={addInvitation}
                className="w-full btn-secondary flex items-center justify-center space-x-2"
              >
                <Plus size={16} />
                <span>Add Another User</span>
              </button>

              {/* Actions */}
              <div className="flex items-center justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || invitations.every(inv => !inv.email.trim())}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {isSubmitting ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Mail size={16} />
                  )}
                  <span>{isSubmitting ? 'Sending Invites...' : 'Send Invites'}</span>
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default InviteUsersModal;
